/**
 * Os quatro eixos de estado de um imóvel, e como cada um vira texto na tela.
 *
 * Eles são independentes e respondem perguntas diferentes:
 *
 * - `commercial_status`  — o imóvel ainda está no mercado? (vendido, alugado…)
 * - `site_publication_status` — aparece no site da imobiliária?
 * - publicação Canal Pro — está anunciado no portal, e em que estado?
 * - `status` (legado) — mistura os três acima com a moderação do formulário
 *   público "Anuncie seu imóvel". Só a parte de moderação continua sem
 *   equivalente moderno, e é a única que a listagem ainda oferece como filtro.
 *
 * Antes, cada tela mantinha seu próprio mapa de rótulos e os filtros repetiam
 * "Vendido"/"Alugado" em dois lugares diferentes. Este módulo é a fonte única.
 */

export type BadgeTone = 'success' | 'warning' | 'danger' | 'neutral';

export interface StatusOption {
  value: string;
  label: string;
}

interface StatusDescriptor {
  label: string;
  tone: BadgeTone;
}

interface CommercialFields {
  commercial_status?: string | null;
  status?: string | null;
}

/** Situação comercial: o imóvel ainda está disponível para negociar? */
const COMMERCIAL_STATUS: Record<string, StatusDescriptor> = {
  available: { label: 'Disponível', tone: 'success' },
  reserved: { label: 'Reservado', tone: 'warning' },
  sold: { label: 'Vendido', tone: 'neutral' },
  rented: { label: 'Alugado', tone: 'neutral' },
  withdrawn: { label: 'Retirado', tone: 'neutral' },
  unknown: { label: 'A revisar', tone: 'warning' },
};

/** Do campo legado para a situação comercial equivalente. */
const LEGACY_TO_COMMERCIAL: Record<string, string> = {
  disponivel: 'available',
  vendido: 'sold',
  alugado: 'rented',
  cancelado: 'withdrawn',
};

/** Publicação no site da imobiliária. */
const SITE_STATUS: Record<string, StatusDescriptor> = {
  published: { label: 'Publicado', tone: 'success' },
  hidden: { label: 'Oculto', tone: 'neutral' },
  draft: { label: 'Rascunho', tone: 'neutral' },
};

/**
 * Estados que o próprio Canal Pro reporta, mais os que descrevem a publicação
 * local. O portal responde em inglês; a listagem nunca deve mostrar o enum cru.
 */
const CHANNEL_STATUS: Record<string, StatusDescriptor> = {
  ACTIVE: { label: 'Ativo', tone: 'success' },
  INACTIVE: { label: 'Inativo', tone: 'neutral' },
  DRAFT: { label: 'Rascunho', tone: 'neutral' },
  BLOCKED: { label: 'Bloqueado', tone: 'danger' },
  PENDING: { label: 'Em análise', tone: 'warning' },
  READY_TO_PUBLISH: { label: 'Pronto para publicar', tone: 'warning' },
  MISSING: { label: 'Removido do canal', tone: 'danger' },
};

/** Como está a entrega entre o site e o canal. */
const SYNC_STATUS: Record<string, StatusDescriptor> = {
  synced: { label: 'Sincronizado', tone: 'success' },
  pending: { label: 'Na fila', tone: 'warning' },
  processing: { label: 'Enviando', tone: 'warning' },
  retry: { label: 'Reenviando', tone: 'warning' },
  pending_confirmation: { label: 'Aguardando confirmação', tone: 'warning' },
  local_pending: { label: 'Alteração local pendente', tone: 'warning' },
  conflict: { label: 'Conflito', tone: 'danger' },
  failed: { label: 'Erro', tone: 'danger' },
  remote_missing: { label: 'Sumiu do canal', tone: 'danger' },
  remote_deleted: { label: 'Excluído do canal', tone: 'neutral' },
};

export const getCommercialStatus = (property: CommercialFields): string => {
  if (property.commercial_status) return property.commercial_status;
  return LEGACY_TO_COMMERCIAL[property.status || ''] || 'unknown';
};

export const commercialStatusBadge = (property: CommercialFields): StatusDescriptor =>
  COMMERCIAL_STATUS[getCommercialStatus(property)] || { label: 'A revisar', tone: 'warning' };

export const getSitePublicationStatus = (property: {
  site_publication_status?: string | null;
  status?: string | null;
}): string => (
  property.site_publication_status || (property.status === 'disponivel' ? 'published' : 'hidden')
);

export const sitePublicationBadge = (property: {
  site_publication_status?: string | null;
  status?: string | null;
}): StatusDescriptor =>
  SITE_STATUS[getSitePublicationStatus(property)] || SITE_STATUS.hidden;

export interface ChannelPublication {
  actual_status?: string | null;
  desired_status?: string | null;
  sync_status?: string | null;
  last_error?: string | null;
}

export const channelStatusLabel = (status?: string | null): string =>
  CHANNEL_STATUS[String(status || '').toUpperCase()]?.label || 'Não enviado';

export const syncStatusLabel = (status?: string | null): string =>
  SYNC_STATUS[String(status || '')]?.label || '';

/**
 * O que mostrar na coluna Publicações para o Canal Pro.
 *
 * Quando desejado e real divergem, o texto diz os dois — é a única forma de a
 * pessoa entender que existe algo em trânsito em vez de achar que o portal
 * ignorou o comando.
 */
export const channelBadge = (publication?: ChannelPublication | null): StatusDescriptor & {
  detail?: string;
} => {
  if (!publication) {
    return { label: 'Não enviado', tone: 'neutral' };
  }

  const actual = String(publication.actual_status || '').toUpperCase();
  const desired = String(publication.desired_status || '').toUpperCase();
  const sync = String(publication.sync_status || '');
  const base = CHANNEL_STATUS[actual];

  if (!base) {
    return {
      label: 'Não enviado',
      tone: sync === 'failed' ? 'danger' : 'neutral',
      detail: publication.last_error || syncStatusLabel(sync) || undefined,
    };
  }

  if (sync === 'conflict' || sync === 'failed') {
    return {
      label: `${base.label} · ${syncStatusLabel(sync)}`,
      tone: 'danger',
      detail: publication.last_error || undefined,
    };
  }

  if (desired && desired !== actual) {
    return {
      label: `${base.label} · indo para ${CHANNEL_STATUS[desired]?.label || desired}`,
      tone: 'warning',
      detail: publication.last_error || undefined,
    };
  }

  return { ...base, detail: publication.last_error || undefined };
};

/** Opções dos selects de filtro, na ordem em que fazem sentido para quem opera. */
export const COMMERCIAL_STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'Qualquer situação' },
  { value: 'available', label: 'Disponível' },
  { value: 'reserved', label: 'Reservado' },
  { value: 'sold', label: 'Vendido' },
  { value: 'rented', label: 'Alugado' },
  { value: 'withdrawn', label: 'Retirado' },
  { value: 'unknown', label: 'A revisar' },
];

export const SITE_STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'Qualquer publicação' },
  { value: 'published', label: 'Publicado' },
  { value: 'hidden', label: 'Oculto' },
  { value: 'draft', label: 'Rascunho' },
];

export const CHANNEL_STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'Qualquer estado' },
  { value: 'active', label: 'Ativo' },
  { value: 'inactive', label: 'Inativo' },
  { value: 'draft', label: 'Rascunho' },
  { value: 'blocked', label: 'Bloqueado' },
  { value: 'missing', label: 'Removido do canal' },
  { value: 'not_sent', label: 'Não enviado' },
  { value: 'syncing', label: 'Em sincronização' },
  { value: 'error', label: 'Com erro ou conflito' },
];

/**
 * O que sobrou do campo legado: moderação do formulário público. Vendido,
 * alugado e disponível saíram daqui de propósito — eles são situação comercial,
 * e ter os dois filtros com as mesmas opções era a origem da confusão.
 */
export const APPROVAL_STATUS_OPTIONS: StatusOption[] = [
  { value: 'all', label: 'Qualquer aprovação' },
  { value: 'pending_approval', label: 'Pendente de aprovação' },
  { value: 'rejected', label: 'Rejeitado' },
];

/** Rótulo curto de um filtro aplicado, para os chips acima da tabela. */
export const filterChipLabel = (field: string, value: string): string | null => {
  if (!value || value === 'all') return null;
  const lookup: Record<string, StatusOption[]> = {
    commercial_status: COMMERCIAL_STATUS_OPTIONS,
    site_publication_status: SITE_STATUS_OPTIONS,
    channel_status: CHANNEL_STATUS_OPTIONS,
    status: APPROVAL_STATUS_OPTIONS,
  };
  const prefix: Record<string, string> = {
    commercial_status: 'Imóvel',
    site_publication_status: 'Site',
    channel_status: 'Canal Pro',
    status: 'Aprovação',
  };
  const option = lookup[field]?.find(item => item.value === value);
  return option ? `${prefix[field]}: ${option.label}` : null;
};
